import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Button,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { InfoApi } from "../../../api/InfoApi";
import { LeaderboardApi } from "../../../api/LeaderboardApi";
import RankingLeaderboard from "./RankingLeaderboard";
import {
    dialogPaperSx,
    dialogTitleSx,
    dialogContentSx,
    iconButtonCloseSx,
    choiceButtonSx,
    mutedTextSx,
} from "../../../styles/cyberTheme";

const LS_KEY = "last_selected_skill_in_top_performer";

const HEADING = "STEAiM-in-PE 比賽 最項目積分排行榜";



// ── Skill Select Dialog ────────────────────────────────────────────────────────

function SkillSelectDialog({ open, onClose, skillList, selectedSkill, onSelect }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: 800,
                    maxWidth: "90vw",
                    ...dialogPaperSx,
                },
            }}
        >
            <DialogTitle sx={dialogTitleSx}>
                選擇項目
                <IconButton onClick={onClose} size="small" sx={iconButtonCloseSx}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ ...dialogContentSx, maxHeight: 400, overflowY: "auto" }}>
                <Grid container spacing={1}>
                    {skillList.map((skill) => {
                        const isActive = selectedSkill?.code === skill.code;
                        return (
                            <Grid item xs={3} key={skill.code}>
                                <Button
                                    fullWidth
                                    onClick={() => onSelect(skill)}
                                    sx={choiceButtonSx(isActive)}
                                >
                                    {skill.name_zh || skill.name_en || skill.code}
                                </Button>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function TopSkillPlayerLeaderboard({
    orgType = "secondary",
    year,
    refreshInterval = -1,
    topRankCount = 3,
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [skillList, setSkillList] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [skillListError, setSkillListError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const orgTypeLabel = orgType === "primary" ? "小學" : orgType === "secondary" ? "中學" : "";

    // ── Reload when orgType / year changes ────────────────────────────────────
    useEffect(() => {
        if (skillList.length > 0 && selectedSkill) {
            loadPerformers(selectedSkill.code, orgType);
        }
    }, [orgType, year]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Load skill list on mount ──────────────────────────────────────────────
    useEffect(() => {
        loadSkillList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSkillList = async () => {
        try {
            const result = await InfoApi.getSteminpeSkillList();
            const list = result || [];
            if (list.length === 0) {
                setSkillListError("未有任何項目清單");
                return;
            }
            setSkillList(list);
            resolveInitialSkill(list);
        } catch {
            setSkillListError("未有任何項目清單");
        }
    };

    const resolveInitialSkill = (list) => {
        const skillParam = searchParams.get("skill");

        let resolved = null;

        if (skillParam) {
            resolved = list.find((s) => s.code === skillParam) ?? null;
        }

        if (!resolved) {
            const lastCode = localStorage.getItem(LS_KEY);
            if (lastCode) {
                resolved = list.find((s) => s.code === lastCode) ?? null;
            }
        }

        if (!resolved) {
            resolved = list[Math.floor(Math.random() * list.length)];
        }

        setSelectedSkill(resolved);
        loadPerformers(resolved.code, orgType);
    };

    // ── Load top performers ───────────────────────────────────────────────────
    const loadPerformers = async (skillCode, currentOrgType = orgType) => {
        setIsLoading(true);
        try {
            const result = await LeaderboardApi.getTopSkillPlayer(skillCode, currentOrgType, year);
            const transformed = (result || []).map(
                ({ id, display_name, org_name, rank, max_score }) => ({
                    id,
                    itemName: `${display_name} (${org_name})`,
                    rank,
                    score: max_score,
                })
            );
            setLeaderboardData(transformed.slice(0, topRankCount));
        } catch {
            setLeaderboardData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Skill change from dialog ──────────────────────────────────────────────
    const handleSkillChange = (skill) => {
        setSelectedSkill(skill);
        setSearchParams({ skill: skill.code });
        localStorage.setItem(LS_KEY, skill.code);
        setDialogOpen(false);
        loadPerformers(skill.code, orgType);
    };

    // ── Error state ───────────────────────────────────────────────────────────
    if (skillListError) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={mutedTextSx}>{skillListError}</Typography>
            </Box>
        );
    }

    return (
        <>
            <RankingLeaderboard
                heading={`${HEADING} (${orgTypeLabel})`}
                subHeading={selectedSkill?.name_zh ?? ""}
                leaderboardData={leaderboardData}
                isLoading={isLoading || !selectedSkill}
                onSubHeadingClick={() => setDialogOpen(true)}
            />

            <SkillSelectDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                skillList={skillList}
                selectedSkill={selectedSkill}
                onSelect={handleSkillChange}
            />
        </>
    );
}
